import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { Messages } from "src/components";
import {
  useGetApplicationSelectionQuoteMutation,
  useUpdateApplicationMoveInDateMutation,
} from "src/store/services/privateApi";
import { parseMoveInTooSoonError } from "src/helpers/moveInTooSoon";
import { formatMonthlyRent, rentsDiffer } from "src/helpers/rentConfirm";

export function MoveInDateCorrection({
  applicationId,
  earliestMoveInDate,
  message,
  unitNumber,
  term,
  currentRent,
  onSaved,
}: {
  applicationId: string;
  earliestMoveInDate: string;
  message: string;
  unitNumber?: string;
  term?: string;
  currentRent?: number | null;
  onSaved: (
    isoDate: string,
    details?: { rent?: number | null; unit?: string }
  ) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [moveInDate, setMoveInDate] = useState(earliestMoveInDate);
  const [saveError, setSaveError] = useState("");
  const [pendingQuote, setPendingQuote] = useState<{
    rent: number;
    previousRent: number | null;
    moveInDate: string;
    term: string | null;
    unit: string | null;
  } | null>(null);
  const [updateMoveInDate, { isLoading: isSaving }] =
    useUpdateApplicationMoveInDateMutation();
  const [getSelectionQuote, { isLoading: isQuoting }] =
    useGetApplicationSelectionQuoteMutation();
  const isBusy = isSaving || isQuoting;

  const persistDate = async (isoDate: string) => {
    const confirmedRent = pendingQuote?.rent ?? currentRent ?? null;
    const res: any = await updateMoveInDate({
      id: applicationId,
      moveInDate: isoDate,
    });
    if (res?.error) {
      const tooSoon = parseMoveInTooSoonError(res.error);
      setSaveError(
        tooSoon?.message ??
          res.error?.data?.message ??
          t("account.leasePayment.moveInTooSoon.saveError")
      );
      setPendingQuote(null);
      return;
    }
    setPendingQuote(null);
    await onSaved(res?.data?.moveInDate ?? isoDate, {
      rent: res?.data?.rent ?? confirmedRent,
      unit: unitNumber,
    });
  };

  const onSave = async () => {
    setSaveError("");
    const quoteRes: any = await getSelectionQuote({
      id: applicationId,
      moveInDate,
      ...(unitNumber ? { unitNumber } : {}),
      ...(term ? { term } : {}),
    });
    if (quoteRes?.error) {
      const tooSoon = parseMoveInTooSoonError(quoteRes.error);
      if (tooSoon) {
        setSaveError(
          tooSoon.message ?? t("account.leasePayment.moveInTooSoon.saveError")
        );
        return;
      }
      // Unit may no longer be quotable; still persist the date so payment can
      // surface unit-unavailable next.
      await persistDate(moveInDate);
      return;
    }
    const rent = Number(quoteRes?.data?.rent);
    const previousRent =
      quoteRes?.data?.previousRent == null
        ? currentRent ?? null
        : Number(quoteRes.data.previousRent);
    if (Number.isFinite(rent) && rentsDiffer(previousRent, rent)) {
      setPendingQuote({
        rent,
        previousRent: Number.isFinite(previousRent as number)
          ? (previousRent as number)
          : null,
        moveInDate: quoteRes?.data?.moveInDate ?? moveInDate,
        term: quoteRes?.data?.term ? String(quoteRes.data.term) : term ?? null,
        unit: quoteRes?.data?.unit
          ? String(quoteRes.data.unit)
          : unitNumber ?? null,
      });
      return;
    }
    await persistDate(moveInDate);
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Messages
        messageHeading={t("account.leasePayment.moveInTooSoon.heading")}
        messageContent={message || t("account.leasePayment.moveInTooSoon.body")}
        bottomMargin={2}
        topMargin={0}
        hideCloseIcon
      />
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t("account.leasePayment.moveInTooSoon.pickDate")}
      </Typography>
      <TextField
        type="date"
        fullWidth
        value={moveInDate}
        inputProps={{ min: earliestMoveInDate }}
        onChange={(e) => setMoveInDate(e.target.value)}
        sx={{ mb: 2 }}
      />
      {saveError !== "" && (
        <Messages
          messageHeading={t("account.leasePayment.moveInTooSoon.heading")}
          messageContent={saveError}
          closeEvent={() => setSaveError("")}
          type="error"
          bottomMargin={2}
          topMargin={0}
        />
      )}
      <Button
        variant="contained"
        onClick={onSave}
        disabled={isBusy || !moveInDate || moveInDate < earliestMoveInDate}
      >
        {t("account.leasePayment.moveInTooSoon.saveAndContinue")}
      </Button>

      <Dialog
        open={pendingQuote != null}
        onClose={() => !isBusy && setPendingQuote(null)}
      >
        <DialogTitle>
          {t("account.leasePayment.moveInTooSoon.confirmRentHeading")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("account.leasePayment.moveInTooSoon.confirmRentBody")}
          </Typography>
          {pendingQuote?.unit && (
            <Typography variant="body2">
              <strong>{t("account.leasePayment.unit")}:</strong>{" "}
              {pendingQuote.unit}
            </Typography>
          )}
          {pendingQuote?.term && (
            <Typography variant="body2">
              <strong>
                {t("account.leasePayment.moveInTooSoon.leaseTerm")}:
              </strong>{" "}
              {t("account.leasePayment.moveInTooSoon.months", {
                term: pendingQuote.term,
              })}
            </Typography>
          )}
          {pendingQuote?.previousRent != null && (
            <Typography variant="body2">
              <strong>
                {t("account.leasePayment.moveInTooSoon.previousRent")}:
              </strong>{" "}
              {formatMonthlyRent(pendingQuote.previousRent)}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mt: 1 }}>
            <strong>{t("account.leasePayment.moveInTooSoon.newRent")}:</strong>{" "}
            {formatMonthlyRent(pendingQuote?.rent)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingQuote(null)} disabled={isBusy}>
            {t("account.leasePayment.moveInTooSoon.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => pendingQuote && persistDate(pendingQuote.moveInDate)}
            disabled={isBusy}
          >
            {isSaving
              ? "Saving…"
              : t("account.leasePayment.moveInTooSoon.confirmRent")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
